/**
 * The open/close shortcut.
 *
 * Strg/Cmd + Punkt toggles the panel. It lives here rather than in the panel component
 * for a structural reason: the shell unmounts the panel when it is closed, so a
 * listener inside the component can never be the thing that opens it. The listener is
 * therefore installed once at module load — the shell imports this module at startup —
 * and drives the shell's own toggle button, which keeps a single source of truth for
 * whether the panel is open.
 *
 * Combining chord is Strg/Cmd + Punkt because Strg K is already the command palette
 * and no browser binds the period. It is a modifier chord, so it stays safe to press
 * inside a grid cell or a text field.
 *
 * Focus is deliberate rather than automatic: opening by keyboard is a request to type,
 * so the input takes focus, and every other way the panel appears leaves the caret
 * where the user put it. A panel that steals focus from a data grid on navigation is
 * worse than no panel.
 */

const TOGGLE_BESCHRIFTUNG = "Assistent";

let installiert = false;
let fokusAnfordern = false;

/** Finds the shell's toggle. Returns null if the shell ever stops rendering it. */
function findeUmschalter(): HTMLButtonElement | null {
  const knoepfe = document.querySelectorAll<HTMLButtonElement>("button[aria-pressed]");
  for (const knopf of knoepfe) {
    if (knopf.textContent?.trim() === TOGGLE_BESCHRIFTUNG) return knopf;
  }
  return null;
}

export function istOffen(): boolean {
  return findeUmschalter()?.getAttribute("aria-pressed") === "true";
}

export function schliesseAssistent(): void {
  if (istOffen()) findeUmschalter()?.click();
}

/** True exactly once per keyboard-triggered open, so the input focuses only then. */
export function nimmFokusAnforderung(): boolean {
  const angefordert = fokusAnfordern;
  fokusAnfordern = false;
  return angefordert;
}

export function installiereTastenkuerzel(): void {
  if (installiert || typeof document === "undefined") return;
  installiert = true;

  document.addEventListener("keydown", (ereignis) => {
    if (ereignis.key !== "." || !(ereignis.metaKey || ereignis.ctrlKey)) return;
    if (ereignis.altKey || ereignis.shiftKey) return;

    const umschalter = findeUmschalter();
    if (!umschalter) return;

    ereignis.preventDefault();
    fokusAnfordern = umschalter.getAttribute("aria-pressed") !== "true";
    umschalter.click();
  });
}

/** The chord, spelled for the header hint. */
export function kuerzelText(): string {
  const mac = typeof navigator !== "undefined" && /Mac|iPhone|iPad/.test(navigator.platform);
  return mac ? "Cmd ." : "Strg .";
}
